/*! Copyright (c) 2026, XAPPmedia */

export interface ClassDictionary {
    [id: string]: unknown;
}

export type ClassValue = string | number | boolean | null | undefined | ClassDictionary | ClassValue[];

/**
 * Joins class names, skipping falsy values.
 *
 * Replaces the `classnames` package. The behavior below was measured against
 * classnames@2.5.1 before removal, because component snapshots in this repo
 * capture rendered class strings — any difference in ordering, spacing or
 * deduplication would show up as spurious snapshot churn:
 *
 * - values join with a single space, in argument order
 * - falsy values are skipped, including `0`, `""`, `false`, `null` and
 *   `undefined` — but a numeric `1` renders as `"1"`
 * - duplicates are **not** removed: `classNames("a", "a")` is `"a a"`
 * - object keys are included when their value is truthy, in key order
 * - arrays are flattened, including nested arrays
 * - no arguments produces an empty string
 *
 * @export
 * @param {...ClassValue[]} args
 * @returns {string}
 */
export function classNames(...args: ClassValue[]): string {
    const classes: string[] = [];

    for (const arg of args) {
        if (!arg) {
            continue;
        }

        if (typeof arg === "string" || typeof arg === "number") {
            classes.push(`${arg}`);
        } else if (Array.isArray(arg)) {
            const nested = classNames(...arg);

            if (nested) {
                classes.push(nested);
            }
        } else if (typeof arg === "object") {
            for (const key of Object.keys(arg)) {
                if (arg[key]) {
                    classes.push(key);
                }
            }
        }
    }

    return classes.join(" ");
}

export default classNames;
